import { Box, Typography, useTheme } from "@mui/material";
import FixedSizeDialog from "components/fixedSizeDialog";
import { useState, useRef } from "react";
import { useSelector }      from "react-redux";
import moment               from "moment";

import ScheduleIcon  from "@mui/icons-material/Schedule";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon    from "@mui/icons-material/Person";
import PhoneIcon     from "@mui/icons-material/Phone";

import FullCalendar      from "@fullcalendar/react";
import dayGridPlugin     from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

import { useGivenPeriodShiftsList } from "api/hooks/humanResource";

const ShiftCalendar = ({ setIsSuccess, setIsFailed }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { givenPeriodShiftList, setFilter } = useGivenPeriodShiftsList(business_branch_id);

  const [open,             setOpen]             = useState(false);
  const [eventAssignments, setEventAssignments] = useState([]);
  const [eventTitle,       setEventTitle]       = useState("");
  const [eventStart,       setEventStart]       = useState(null);
  const [eventEnd,         setEventEnd]         = useState(null);

  const calendarRef = useRef();

  /* Build FullCalendar event array */
  const events = [];
  givenPeriodShiftList.forEach((element) => {
    element.shifts.forEach((shift) => {
      events.push({
        title: shift.detail.shift_name,
        start: `${element.day} ${shift.detail.start_time}`,
        end:   `${element.day} ${shift.detail.end_time}`,
        extendedProps: { assignments: shift.assignments },
      });
    });
  });

  const handleEventClick = (info) => {
    setEventAssignments(info.event.extendedProps.assignments);
    setEventTitle(info.event.title);
    setEventStart(info.event.start);
    setEventEnd(info.event.end);
    setOpen(true);
  };

  /* ── FullCalendar theme injection ── */
  const calendarStyles = `
    .fc { font-family: 'DM Sans', sans-serif; font-size: 13px; }
    .fc .fc-toolbar-title { font-family: 'DM Serif Display', serif; font-size: 18px; font-weight: 400; color: ${textPri}; }
    .fc .fc-button {
      background-color: transparent !important;
      border: 1px solid ${border} !important;
      color: ${textSec} !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      border-radius: 8px !important;
      padding: 4px 12px !important;
      text-transform: none !important;
      box-shadow: none !important;
      transition: all 0.15s !important;
    }
    .fc .fc-button:hover {
      background-color: rgba(29,158,117,0.08) !important;
      border-color: ${accent} !important;
      color: ${accent} !important;
    }
    .fc .fc-button-active, .fc .fc-button-primary:not(:disabled).fc-button-active {
      background-color: rgba(29,158,117,0.12) !important;
      border-color: ${accent} !important;
      color: ${accent} !important;
    }
    .fc-theme-standard td, .fc-theme-standard th, .fc-theme-standard .fc-scrollgrid {
      border-color: ${border} !important;
    }
    .fc .fc-col-header-cell-cushion {
      font-size: 11px; font-weight: 600;
      text-transform: uppercase; letter-spacing: 0.06em;
      color: ${textSec}; text-decoration: none;
    }
    .fc .fc-daygrid-day-number {
      font-size: 12px; color: ${textSec}; text-decoration: none;
    }
    .fc .fc-daygrid-day.fc-day-today { background-color: rgba(29,158,117,0.07) !important; }
    .fc .fc-daygrid-day:hover { background-color: rgba(29,158,117,0.04) !important; }
    .fc-event {
      background-color: ${accent} !important;
      border: none !important;
      border-radius: 5px !important;
      padding: 1px 5px !important;
      font-size: 11px !important;
      font-weight: 500 !important;
      cursor: pointer !important;
    }
    .fc-event:hover { background-color: #0F6E56 !important; }
    .fc-event-title { color: #fff !important; }
    .fc-event-time  { color: rgba(255,255,255,0.75) !important; font-size: 10px !important; }
    .fc .fc-daygrid-body { background-color: ${cardBg}; }
    .fc-scrollgrid { background-color: ${cardBg}; }
  `;

  /* ── Dialog content ── */
  const dialogContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

      {/* Time banner */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "10px",
        p: "0.875rem 1rem",
        backgroundColor: "rgba(29,158,117,0.07)",
        border: `1px solid rgba(29,158,117,0.2)`,
        borderRadius: "8px",
      }}>
        <AccessTimeIcon sx={{ fontSize: "18px", color: accent, flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "2px" }}>
            Shift Time
          </Typography>
          <Typography sx={{ fontSize: "13px", color: textPri, fontWeight: 500 }}>
            {eventStart ? moment(eventStart).format("LL") : "—"}
            {" · "}
            {eventStart ? moment(eventStart).format("LT") : ""} –{" "}
            {eventEnd   ? moment(eventEnd).format("LT")   : "Next day"}
          </Typography>
        </Box>
      </Box>

      {/* Employee list */}
      <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden" }}>
        <Box sx={{ px: "1.25rem", py: "0.75rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)", display: "flex", alignItems: "center", gap: "6px" }}>
          <PersonIcon sx={{ fontSize: "14px", color: accent }} />
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
            Employees Assigned ({eventAssignments.length})
          </Typography>
        </Box>

        {eventAssignments.length === 0 ? (
          <Box sx={{ p: "1.5rem", textAlign: "center" }}>
            <Typography sx={{ fontSize: "13px", color: textSec, fontStyle: "italic" }}>No employees assigned to this shift</Typography>
          </Box>
        ) : (
          <Box>
            {eventAssignments.map((row, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: "1.25rem", py: "0.65rem",
                  borderBottom: index < eventAssignments.length - 1 ? `1px solid ${border}` : "none",
                  backgroundColor: index % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)"),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {/* Avatar */}
                  <Box sx={{
                    width: "30px", height: "30px", borderRadius: "50%",
                    backgroundColor: "rgba(29,158,117,0.12)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: accent, flexShrink: 0, fontSize: "12px", fontWeight: 600,
                  }}>
                    {row.related_employee.first_name?.[0]}{row.related_employee.last_name?.[0]}
                  </Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                    {row.related_employee.first_name} {row.related_employee.last_name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: textSec }}>
                  <PhoneIcon sx={{ fontSize: "13px" }} />
                  <Typography sx={{ fontSize: "12px" }}>{row.related_employee.phone_number}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* Inject calendar styles */}
      <style>{calendarStyles}</style>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
          <ScheduleIcon sx={{ fontSize: "22px" }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Shift Calendar</Typography>
          <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>Click a shift to view assigned employees</Typography>
        </Box>
      </Box>

      {/* Calendar card */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        overflow: "hidden",
        p: "1.25rem",
      }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left:   "prev,next today",
            center: "title",
            right:  "dayGridDay,dayGridWeek,dayGridMonth",
          }}
          height="78vh"
          events={events}
          eventClick={handleEventClick}
          datesSet={(dateInfo) => {
            setFilter({
              start: moment(dateInfo.start).format("YYYY-MM-DD"),
              end:   moment(dateInfo.end).format("YYYY-MM-DD"),
            });
          }}
        />
      </Box>

      {/* Shift detail dialog */}
      <FixedSizeDialog
        open={open}
        handleClose={() => setOpen(false)}
        dialogHeading={eventTitle ? `${eventTitle} — Shift Assignment` : "Shift Assignment"}
        negativeResponse="Close"
        maxWidth="sm"
        content={dialogContent}
      />
    </Box>
  );
};

export default ShiftCalendar;